import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { container } from 'tsyringe';

import { SystemType } from '@/v1/main/domain/auth';
import { AuthServiceImpl } from '@/v1/main/usecases';
import { PageNotFound } from '@/v1/main/views//Result';
import {
    MessageOption,
    MessageType,
    useAuth,
    useDevice,
    useMessage,
} from '@/v1/main/views/Provider';

export const ProtectedRoute: React.FC = () => {
    const { t } = useTranslation(['interface', 'title']);
    const usecase = container.resolve(AuthServiceImpl);

    const navigate = useNavigate();
    const location = useLocation();
    const { sys } = useParams();

    const { isMobileMode } = useDevice();
    const { loggedIn, menus, setPageId } = useAuth();
    const { setMessageOption } = useMessage();

    const [denying, setDenying] = useState<boolean>(false);
    const [landing, setLanding] = useState<string>(`/${sys}/login`);
    const [routeMap, setRouteMap] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (!sys) return;
        document.title = t<string>(`system.${sys}`, { ns: 'title' });
    }, [t, sys]);

    // 修正 sys
    useEffect(() => {
        let redirect = SystemType.WEB;
        if (isMobileMode) {
            redirect = SystemType.MOB;
        }
        if (sys != redirect) {
            usecase.setSystemType(redirect);
            navigate(`/${redirect}/login`);
        }
    }, [isMobileMode, sys]);

    // 獲取頁面權限後組路由驗證的資料
    useEffect(() => {
        if (!sys) return;
        const map = new Map();
        for (const menu of menus) {
            if (!menu.children) continue;
            const route_prefix = `/${sys}/${menu.programName}/`;
            for (const submenu of menu.children) {
                const route = route_prefix + submenu.programName;
                map.set(route, submenu.id);
            }
        }
        if (map.size > 0 && sys == SystemType.MOB) {
            map.set(`/${sys}/setting`, 'setting');
        }
        const landing_page = map.size > 0 ? map.keys().next().value : '';
        setRouteMap(map);
        setLanding(landing_page);
    }, [menus]);

    // 根據 landing 跳轉
    useEffect(() => {
        const isLoginPage = location.pathname === `/${sys}/login`;
        if (!loggedIn && !isLoginPage) {
            navigate(`/${sys}/login`);
        } else if (loggedIn && isLoginPage) {
            const opt: MessageOption = {
                type: MessageType.INFO,
                content: t('message.login_redirect', {
                    ns: 'interface',
                }),
            };
            setMessageOption(opt);

            landing && navigate(landing);
        }
    }, [landing]);

    // 驗證當前路徑權限
    useEffect(() => {
        if (routeMap.size == 0) return;
        setDenying(false);
        const regex = new RegExp('/(upload|sort)$', 'g');
        const currentPath = location.pathname.replace(regex, '');
        const isLoginPage = currentPath.endsWith('login');
        if (isLoginPage) navigate(landing);
        else {
            const id = routeMap.get(currentPath);
            if (id) {
                setPageId(id);
            } else {
                setDenying(true);
                const opt: MessageOption = {
                    type: MessageType.ERROR,
                    content: t('message.page_access_denied', {
                        ns: 'interface',
                    }),
                };
                setMessageOption(opt);
                setTimeout(() => {
                    navigate(landing);
                }, 5000);
            }
        }
    }, [location.pathname, routeMap]);

    return <>{denying ? <PageNotFound /> : <Outlet />}</>;
};
